import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MuxPlayer from '@mux/mux-player-react';

import Alarm2Line from '@/assets/icons/alarm_2_line.svg?component';
import CloseCircleLine from '@/assets/icons/close_circle_line.svg?component';
import TimeDurationLine from '@/assets/icons/time_duration_line.svg?component';
import VideoLoadingSpinner from '@/assets/icons/video_loading_spinner.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import settings from '@/config/settings';
import videoThumbnail from '@/svg/videoThumbnail.jpg';

import styles from './VideoBox.module.scss';
/** Live attitude readings a bioscope reports while it streams, as the device sends them. */
export interface VideoBoxReadings {
    depth?: number | string;
    pitch?: number | string;
    roll?: number | string;
    waterTemperature?: number | string;
}

export interface VideoBoxProps {
    cageName?: string;
    videoState: string;
    videoSrc?: string;
    timeRemaining?: number;
    userId?: number;
    videoId?: string;
    bioscopeData?: VideoBoxReadings;
}

function VideoBox({
    cageName = '',
    videoState = '',
    videoSrc = '',
    timeRemaining = 0,
    userId = 0,
    videoId = '',
    bioscopeData = {},
}: VideoBoxProps) {
    const { t } = useTranslation();
    const [videoCountDown, setVideoCountDown] = useState<string>('');
    useEffect(() => {
        const interval = setInterval(() => {
            if (timeRemaining > 0) {
                const minutes = Math.floor(timeRemaining / 60);
                const seconds = timeRemaining % 60;
                setVideoCountDown(`${minutes}:${seconds < 10 ? '0' : ''}${seconds}`);
                timeRemaining--;
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [timeRemaining]);

    const renderContent = () => {
        if (
            // videoState === 'idle' ||
            videoState !== 'active'
        ) {
            return (
                <>
                    <div className={styles.muxPlayerWrapper}>
                        <div className={styles.stateContainer}>
                            {/* <img className={styles.videoThumbnail} src={videoThumbnail} alt="" /> */}
                        </div>
                        <div className={styles.overlayContent}>
                            <SvgIcon icon={VideoLoadingSpinner} size="custom" customSize={52} />
                            {/* <div className={styles.message}>
                                {t('molecules.videoBox.loading')}
                            </div> */}
                        </div>
                    </div>
                    <div className={styles.videoOverlay}>
                        <div>{cageName}</div>
                    </div>
                </>
            );
        }
        if (videoState === 'active') {
            return (
                <>
                    <MuxPlayer
                        playbackId={videoSrc}
                        accentColor={settings.primaryColor}
                        autoPlay={true}
                        metadata={{
                            video_id: videoId,
                            video_title: cageName,
                            viewer_user_id: userId,
                        }}
                    />
                    <div className={styles.videoOverlay}>
                        <div>{cageName}</div>
                        <div className={styles.cageOverlay}>
                            <div className={styles.timer}>
                                <SvgIcon icon={TimeDurationLine} size="custom" customSize={20} />
                                <span>{videoCountDown}</span>
                            </div>
                            {bioscopeData && (
                                <>
                                    <div>
                                        {t('common.labels.depth')}: {bioscopeData.depth}
                                    </div>
                                    <div>
                                        {t('molecules.videoBox.pitch')}: {bioscopeData.pitch}
                                    </div>
                                    <div>
                                        {t('molecules.videoBox.roll')}: {bioscopeData.roll}
                                    </div>
                                    <div>
                                        {t('molecules.videoBox.temperatureShort')}:{' '}
                                        {bioscopeData.waterTemperature}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </>
            );
        }
        if (videoState === 'error' || videoState === 'disconnected') {
            return (
                <>
                    <div className={styles.stateContainer}>
                        <SvgIcon
                            icon={CloseCircleLine}
                            color={settings.darkGrayColor}
                            size="custom"
                            customSize={52}
                        />
                        <div className={styles.message}>{t('molecules.videoBox.error')}</div>
                    </div>
                    <div className={styles.videoOverlay}>
                        <div>{cageName}</div>
                    </div>
                </>
            );
        }
        if (videoState === 'timeout') {
            return (
                <>
                    <div className={styles.stateContainer}>
                        <SvgIcon
                            icon={Alarm2Line}
                            color={settings.darkGrayColor}
                            size="custom"
                            customSize={52}
                        />
                        <div className={styles.message}>
                            {t('molecules.videoBox.dailyLimitReached')}
                        </div>
                    </div>
                    <div className={styles.videoOverlay}>
                        <div>{cageName}</div>
                        <div className={styles.timer}>
                            <SvgIcon
                                icon={TimeDurationLine}
                                color={settings.warningColor}
                                size="custom"
                                customSize={20}
                            />
                            <span className={styles.timeout}>{0}</span>
                        </div>
                    </div>
                </>
            );
        }
        return (
            <>
                <div className={styles.muxPlayerWrapper}>
                    <div className={styles.stateContainer}>
                        <img className={styles.videoThumbnail} src={videoThumbnail} alt="" />
                    </div>
                    <div className={styles.overlayContent}>
                        <SvgIcon icon={VideoLoadingSpinner} size="custom" customSize={52} />
                        <div className={styles.message}>{t('molecules.videoBox.loading')}</div>
                    </div>
                </div>
                <div className={styles.videoOverlay}>
                    <div>{cageName}</div>
                </div>
            </>
        );
    };
    // console.log('videoId', videoId);
    return <div className={styles.videoBox}>{renderContent()}</div>;
}

export default VideoBox;
export { VideoBox };
